<!-- eslint-disable vuetify/no-deprecated-colors -->
<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';

import { FlowPhaseOffsetCorrection } from '../store/calculators/FlowPhaseOffsetCorrection';

const props = defineProps({
  show: { type: Boolean, required: true },
  phaseFrames: { type: Array, required: true },
  magnitudeFrames: { type: Array, required: true },
  imageWidth: { type: Number, required: true },
  imageHeight: { type: Number, required: true },
  venc: { type: Number, required: true },
  initialThreshold: { type: Number, default: 5.0 },
});

const emit = defineEmits(['close', 'apply']);

// Refs
const phaseCanvas = ref(null);
const phaseMaskCanvas = ref(null);
const magCanvas = ref(null);
const magMaskCanvas = ref(null);
const offsetCanvas = ref(null);

// State
const currentThreshold = ref(props.initialThreshold);
const polynomialOrder = ref(2);
const imageBounds = ref({
  leftOffset: 0,
  rightOffset: 0,
  topOffset: 0,
  bottomOffset: 0,
});
const correctionResult = ref(null);
const timeAvgPhase = ref(null);
const timeAvgMag = ref(null);
const percentiles = ref({ p10: 0, p50: 0, p90: 0 });
const activeBoundary = ref(null);

// Computed
const polynomialOrderHint = computed(() => {
  const hints = {
    1: 'Linear (3 coeff)',
    2: 'Quadratic (6 coeff)',
    3: 'Cubic (10 coeff)',
  };
  return hints[polynomialOrder.value];
});

const staticPixelCount = computed(() =>
  correctionResult.value ? correctionResult.value.metrics.staticTissueCount : 0,
);

const staticPercentage = computed(
  () => (staticPixelCount.value / (props.imageWidth * props.imageHeight)) * 100,
);

const offsetStats = computed(() => {
  if (!correctionResult.value) return null;

  const surface = correctionResult.value.offsetSurface;
  const n = props.imageWidth * props.imageHeight;
  let sum = 0;
  let min = Infinity;
  let max = -Infinity;
  let maxAbs = 0;

  for (let i = 0; i < n; i++) {
    const val = surface[i];
    sum += val;
    if (val < min) min = val;
    if (val > max) max = val;
    if (Math.abs(val) > maxAbs) maxAbs = Math.abs(val);
  }

  const mean = sum / n;
  let variance = 0;
  for (let i = 0; i < n; i++) {
    variance += (surface[i] - mean) ** 2;
  }

  return { min, max, mean, std: Math.sqrt(variance / n), maxAbs };
});

// Methods
const computePercentiles = () => {
  const n = props.imageWidth * props.imageHeight;
  const numFrames = props.phaseFrames.length;

  timeAvgPhase.value = new Float32Array(n);
  timeAvgMag.value = new Float32Array(n);

  for (let i = 0; i < n; i++) {
    let pSum = 0;
    let mSum = 0;
    for (let t = 0; t < numFrames; t++) {
      pSum += props.phaseFrames[t][i];
      mSum += props.magnitudeFrames[t][i];
    }
    timeAvgPhase.value[i] = pSum / numFrames;
    timeAvgMag.value[i] = mSum / numFrames;
  }

  const phaseStd = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const mean = timeAvgPhase.value[i];
    let variance = 0;
    for (let t = 0; t < numFrames; t++) {
      variance += (props.phaseFrames[t][i] - mean) ** 2;
    }
    phaseStd[i] = Math.sqrt(variance / numFrames);
  }

  const sortedStd = Array.from(phaseStd).sort((a, b) => a - b);
  percentiles.value = {
    p10: sortedStd[Math.floor(n * 0.1)],
    p50: sortedStd[Math.floor(n * 0.5)],
    p90: sortedStd[Math.floor(n * 0.9)],
  };
};

const runCorrection = () => {
  if (!props.phaseFrames.length || !props.magnitudeFrames.length) return;

  const corrector = new FlowPhaseOffsetCorrection({
    width: props.imageWidth,
    height: props.imageHeight,
    venc: props.venc,
    polynomialOrder: polynomialOrder.value,
    thresholds: {
      magnitudePercentile: 0.99,
      temporalStdThreshold: currentThreshold.value,
      minPixelCount: 30,
    },
    bounds: imageBounds.value,
  });

  correctionResult.value = corrector.correctPhaseOffset(
    props.phaseFrames,
    props.magnitudeFrames,
  );
};

const renderBaseImages = () => {
  if (
    !phaseCanvas.value
    || !magCanvas.value
    || !timeAvgPhase.value
    || !timeAvgMag.value
  ) {
    return;
  }

  const n = props.imageWidth * props.imageHeight;

  // Phase
  const phaseCtx = phaseCanvas.value.getContext('2d');
  const phaseMin = Math.min(...timeAvgPhase.value);
  const phaseMax = Math.max(...timeAvgPhase.value);
  const phaseRange = phaseMax - phaseMin || 1;
  const phaseImgData = phaseCtx.createImageData(
    props.imageWidth,
    props.imageHeight,
  );
  for (let i = 0; i < n; i++) {
    const val = Math.min(
      255,
      Math.max(0, (255 * (timeAvgPhase.value[i] - phaseMin)) / phaseRange),
    );
    const base = 4 * i;
    phaseImgData.data[base] = val;
    phaseImgData.data[base + 1] = val;
    phaseImgData.data[base + 2] = val;
    phaseImgData.data[base + 3] = 255;
  }
  phaseCtx.putImageData(phaseImgData, 0, 0);

  // Magnitude
  const magCtx = magCanvas.value.getContext('2d');
  const magMin = Math.min(...timeAvgMag.value);
  const magMax = Math.max(...timeAvgMag.value);
  const magRange = magMax - magMin || 1;
  const magImgData = magCtx.createImageData(
    props.imageWidth,
    props.imageHeight,
  );
  for (let i = 0; i < n; i++) {
    const val = Math.min(
      255,
      Math.max(0, (255 * (timeAvgMag.value[i] - magMin)) / magRange),
    );
    const base = 4 * i;
    magImgData.data[base] = val;
    magImgData.data[base + 1] = val;
    magImgData.data[base + 2] = val;
    magImgData.data[base + 3] = 255;
  }
  magCtx.putImageData(magImgData, 0, 0);
};

const updateMaskOverlay = () => {
  if (!phaseMaskCanvas.value || !magMaskCanvas.value || !correctionResult.value)
    return;

  const n = props.imageWidth * props.imageHeight;
  const mask = correctionResult.value.staticTissueMask;
  const boundsExcluded = correctionResult.value.excludedMasks?.boundsExcluded;

  const renderMask = (canvas) => {
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, props.imageWidth, props.imageHeight);
    const imgData = ctx.createImageData(props.imageWidth, props.imageHeight);

    for (let i = 0; i < n; i++) {
      let r;
      let g;
      let b;
      let a;
      if (boundsExcluded && boundsExcluded[i]) {
        [r, g, b, a] = [255, 165, 0, 120]; // Orange
      }
      else if (mask[i]) {
        [r, g, b, a] = [0, 255, 0, 100]; // Green
      }
      else {
        [r, g, b, a] = [255, 0, 0, 80]; // Red
      }
      imgData.data[4 * i] = r;
      imgData.data[4 * i + 1] = g;
      imgData.data[4 * i + 2] = b;
      imgData.data[4 * i + 3] = a;
    }
    ctx.putImageData(imgData, 0, 0);
  };

  renderMask(phaseMaskCanvas.value);
  renderMask(magMaskCanvas.value);
};

const renderOffsetField = () => {
  if (!offsetCanvas.value || !correctionResult.value) return;

  const ctx = offsetCanvas.value.getContext('2d');
  const surface = correctionResult.value.offsetSurface;
  const n = props.imageWidth * props.imageHeight;
  const imgData = ctx.createImageData(props.imageWidth, props.imageHeight);
  const maxAbs = offsetStats.value?.maxAbs || 1;

  for (let i = 0; i < n; i++) {
    const val = surface[i] / maxAbs;
    const t = Math.abs(val);
    const r = val < 0 ? Math.floor(255 * (1 - t)) : 255;
    const g = Math.floor(255 * (1 - t));
    const b = val < 0 ? 255 : Math.floor(255 * (1 - t));

    imgData.data[4 * i] = r;
    imgData.data[4 * i + 1] = g;
    imgData.data[4 * i + 2] = b;
    imgData.data[4 * i + 3] = 255;
  }

  ctx.putImageData(imgData, 0, 0);
};

const updateVisualization = () => {
  runCorrection();
  updateMaskOverlay();
  renderOffsetField();
};

// Drag handlers
const startDrag = (boundary, event) => {
  event.preventDefault();
  activeBoundary.value = boundary;
};

const handleCanvasMouseDown = (event) => {
  if (activeBoundary.value) return;

  const rect = phaseCanvas.value.getBoundingClientRect();
  const x = (event.clientX - rect.left) * (props.imageWidth / rect.width);
  const y = (event.clientY - rect.top) * (props.imageHeight / rect.height);

  const thresh = 10;
  if (Math.abs(x - imageBounds.value.leftOffset) < thresh)
    activeBoundary.value = 'left';
  else if (
    Math.abs(x - (props.imageWidth - imageBounds.value.rightOffset)) < thresh
  )
    activeBoundary.value = 'right';
  else if (Math.abs(y - imageBounds.value.topOffset) < thresh)
    activeBoundary.value = 'top';
  else if (
    Math.abs(y - (props.imageHeight - imageBounds.value.bottomOffset)) < thresh
  )
    activeBoundary.value = 'bottom';
};

const handleMouseMove = (event) => {
  if (!activeBoundary.value) return;

  const rect = phaseCanvas.value.getBoundingClientRect();
  const x = (event.clientX - rect.left) * (props.imageWidth / rect.width);
  const y = (event.clientY - rect.top) * (props.imageHeight / rect.height);

  const clamp = (val, max) => Math.round(Math.max(0, Math.min(val, max * 0.4)));

  switch (activeBoundary.value) {
    case 'right':
      imageBounds.value.rightOffset = clamp(
        props.imageWidth - x,
        props.imageWidth,
      );
      break;
    case 'top':
      imageBounds.value.topOffset = clamp(y, props.imageHeight);
      break;
    case 'bottom':
      imageBounds.value.bottomOffset = clamp(
        props.imageHeight - y,
        props.imageHeight,
      );
      break;
    default:
      break;
  }
};

const handleMouseUp = () => {
  if (activeBoundary.value) {
    activeBoundary.value = null;
    updateVisualization();
  }
};

const resetBounds = () => {
  imageBounds.value = {
    leftOffset: 0,
    rightOffset: 0,
    topOffset: 0,
    bottomOffset: 0,
  };
};

const setPreset = (value) => {
  currentThreshold.value = value;
};

const closeModal = () => emit('close');

const applyThreshold = () => {
  emit('apply', {
    threshold: currentThreshold.value,
    bounds: { ...imageBounds.value },
    polynomialOrder: polynomialOrder.value,
  });
  closeModal();
};

// Watchers
watch(currentThreshold, updateVisualization);
watch(polynomialOrder, updateVisualization);
watch(imageBounds, updateVisualization, { deep: true });

watch(
  () => props.show,
  async (newVal) => {
    if (newVal) {
      imageBounds.value = {
        leftOffset: 0,
        rightOffset: 0,
        topOffset: 0,
        bottomOffset: 0,
      };
      computePercentiles();
      await nextTick();
      renderBaseImages();
      updateVisualization();
    }
  },
);

onMounted(() => {
  if (props.show) {
    computePercentiles();
    nextTick(() => {
      renderBaseImages();
      updateVisualization();
    });
  }
});
</script>

<template>
  <div
    v-if="show"
    class="threshold-tuning-modal"
    role="dialog"
    tabindex="-1"
    @click.self="closeModal"
    @keydown.esc="closeModal"
  >
    <div class="threshold-panel">
      <!-- Header -->
      <div class="panel-header">
        <h2>Static Tissue Correction</h2>
        <button class="close-btn" @click="closeModal">
          ✕
        </button>
      </div>
      <!-- Actions -->
      <div class="actions">
        <button class="btn-secondary-sm" @click="closeModal">
          Cancel
        </button>
        <button class="btn-primary-sm" @click="applyThreshold">
          Apply ({{ currentThreshold.toFixed(2) }} cm/s, Order
          {{ polynomialOrder }})
        </button>
      </div>
      <!-- Image Grid -->
      <div class="image-grid">
        <!-- Phase Image -->
        <div class="image-panel">
          <div class="image-title-compact">
            Phase (Time-Avg)
          </div>
          <div
            class="canvas-wrapper-interactive"
            @mousedown="handleCanvasMouseDown"
            @mousemove="handleMouseMove"
            @mouseup="handleMouseUp"
            @mouseleave="handleMouseUp"
            @blur="handleMouseUp"
          >
            <canvas
              ref="phaseCanvas"
              :width="imageWidth"
              :height="imageHeight"
            />
            <canvas
              ref="phaseMaskCanvas"
              class="mask-overlay"
              :width="imageWidth"
              :height="imageHeight"
            />
            <svg
              class="boundary-overlay"
              :viewBox="`0 0 ${imageWidth} ${imageHeight}`"
            >
              <line
                :x1="imageBounds.leftOffset"
                :y1="0"
                :x2="imageBounds.leftOffset"
                :y2="imageHeight"
                class="boundary-line"
                :class="{ 'boundary-active': activeBoundary === 'left' }"
                @mousedown.stop="startDrag('left', $event)"
              />
              <line
                :x1="imageWidth - imageBounds.rightOffset"
                :y1="0"
                :x2="imageWidth - imageBounds.rightOffset"
                :y2="imageHeight"
                class="boundary-line"
                :class="{ 'boundary-active': activeBoundary === 'right' }"
                @mousedown.stop="startDrag('right', $event)"
              />
              <line
                :x1="0"
                :y1="imageBounds.topOffset"
                :x2="imageWidth"
                :y2="imageBounds.topOffset"
                class="boundary-line"
                :class="{ 'boundary-active': activeBoundary === 'top' }"
                @mousedown.stop="startDrag('top', $event)"
              />
              <line
                :x1="0"
                :y1="imageHeight - imageBounds.bottomOffset"
                :x2="imageWidth"
                :y2="imageHeight - imageBounds.bottomOffset"
                class="boundary-line"
                :class="{ 'boundary-active': activeBoundary === 'bottom' }"
                @mousedown.stop="startDrag('bottom', $event)"
              />

              <text
                :x="imageBounds.leftOffset + 3"
                :y="imageHeight / 2"
                class="boundary-label"
              >
                L:{{ imageBounds.leftOffset }}
              </text>
              <text
                :x="imageWidth - imageBounds.rightOffset - 30"
                :y="imageHeight / 2 - 12"
                class="boundary-label"
              >
                R:{{ imageBounds.rightOffset }}
              </text>
              <text
                x="3"
                :y="imageBounds.topOffset + 12"
                class="boundary-label"
              >
                T:{{ imageBounds.topOffset }}
              </text>
              <text
                x="3"
                :y="imageHeight - imageBounds.bottomOffset - 3"
                class="boundary-label"
              >
                B:{{ imageBounds.bottomOffset }}
              </text>
            </svg>
          </div>
          <div class="legend-compact">
            <div class="legend-item-sm">
              <div class="dot bg-green" />
              Static
            </div>
            <div class="legend-item-sm">
              <div class="dot bg-red" />
              Excluded
            </div>
            <div class="legend-item-sm">
              <div class="dot bg-orange" />
              Bounds
            </div>
          </div>
        </div>

        <!-- Magnitude Image -->
        <div class="image-panel">
          <div class="image-title-compact">
            Magnitude (Time-Avg)
          </div>
          <div class="canvas-wrapper">
            <canvas
              ref="magCanvas"
              :width="imageWidth"
              :height="imageHeight"
            />
            <canvas
              ref="magMaskCanvas"
              class="mask-overlay"
              :width="imageWidth"
              :height="imageHeight"
            />
          </div>
          <div class="legend-compact">
            <div class="legend-item-sm">
              <div class="dot bg-green" />
              Static
            </div>
            <div class="legend-item-sm">
              <div class="dot bg-red" />
              Excluded
            </div>
          </div>
        </div>

        <!-- Offset Field -->
        <div class="image-panel">
          <div class="image-title-compact">
            Offset Surface
            <span v-if="offsetStats" class="range-hint">[{{ offsetStats.min.toFixed(2) }},
              {{ offsetStats.max.toFixed(2) }}]</span>
          </div>
          <div class="canvas-wrapper">
            <canvas
              ref="offsetCanvas"
              :width="imageWidth"
              :height="imageHeight"
            />
          </div>
          <div class="legend-compact">
            <div class="legend-item-sm">
              <div class="gradient-bar" />
              <span>Blue → White (0) → Red</span>
            </div>
          </div>
        </div>
      </div>
      <!-- Compact Controls -->
      <div class="control-section-compact">
        <!-- Compact Stats -->
        <div class="stats-compact">
          <span class="stat-compact">
            <strong>Static:</strong> {{ staticPixelCount.toLocaleString() }} ({{
              staticPercentage.toFixed(1)
            }}%)
          </span>
          <span v-if="correctionResult" class="stat-compact">
            <strong>RMS:</strong>
            {{ correctionResult.metrics.rmsError.toFixed(4) }} cm/s
          </span>
          <span v-if="offsetStats" class="stat-compact">
            <strong>Mean:</strong> {{ offsetStats.mean >= 0 ? '+' : ''
            }}{{ offsetStats.mean.toFixed(4) }} cm/s
          </span>
          <span v-if="offsetStats" class="stat-compact">
            <strong>Max:</strong> {{ offsetStats.maxAbs.toFixed(3) }} cm/s
          </span>
          <span class="stat-compact">
            <strong>Size:</strong> {{ imageWidth }}×{{ imageHeight }}
          </span>
          <span class="stat-compact">
            <strong>VENC:</strong> ±{{ venc }} cm/s
          </span>
        </div>
        <div class="controls-row">
          <!-- Polynomial Order -->
          <div class="control-group-compact">
            <span class="label-compact">Polynomial Order</span>
            <div class="order-buttons">
              <button
                v-for="order in [1, 2, 3]"
                :key="order"
                class="order-btn"
                :class="{ active: polynomialOrder === order }"
                @click="polynomialOrder = order"
              >
                {{ order }}
              </button>
            </div>
            <div class="hint-compact">
              {{ polynomialOrderHint }}
            </div>
          </div>

          <!-- Temporal Threshold Slider -->
          <div class="control-group-compact flex-grow">
            <div class="slider-header">
              <span class="label-compact">Temporal Std Dev Threshold</span>
              <span class="value-compact">{{ currentThreshold.toFixed(2) }} cm/s</span>
            </div>
            <input
              id="threshold-slider"
              v-model.number="currentThreshold"
              type="range"
              aria-label="Threshold"
              :min="percentiles.p10"
              :max="percentiles.p90"
              :step="(percentiles.p90 - percentiles.p10) / 100"
              class="slider-compact"
            >
            <div class="markers-compact">
              <span>{{ percentiles.p10.toFixed(2) }}</span>
              <span>p50: {{ percentiles.p50.toFixed(2) }}</span>
              <span>{{ percentiles.p90.toFixed(2) }}</span>
            </div>
          </div>

          <!-- Quick Presets -->
          <div class="control-group-compact">
            <span class="label-compact">Quick Presets</span>
            <div class="preset-row">
              <button class="btn-xs" @click="setPreset(percentiles.p10)">
                p10
              </button>
              <button class="btn-xs" @click="setPreset(percentiles.p50)">
                p50
              </button>
              <button class="btn-xs" @click="setPreset(percentiles.p90)">
                p90
              </button>
            </div>
          </div>
        </div>

        <!-- Image Bounds -->
        <div class="bounds-compact">
          <div class="bounds-header">
            <span class="label-compact">Image Bounds (drag orange lines or use sliders)</span>
            <button class="btn-reset" @click="resetBounds">
              Reset
            </button>
          </div>
          <div class="bounds-row">
            <div class="bound-item">
              <span>L: {{ imageBounds.leftOffset }}</span>
              <input
                v-model.number="imageBounds.leftOffset"
                type="range"
                aria-label="Left bound offset"
                :min="0"
                :max="Math.floor(imageWidth * 0.4)"
                class="slider-xs"
              >
            </div>
            <div class="bound-item">
              <span>R: {{ imageBounds.rightOffset }}</span>
              <input
                v-model.number="imageBounds.rightOffset"
                type="range"
                aria-label="Right bound offset"
                :min="0"
                :max="Math.floor(imageWidth * 0.4)"
                class="slider-xs"
              >
            </div>
            <div class="bound-item">
              <span>T: {{ imageBounds.topOffset }}</span>
              <input
                v-model.number="imageBounds.topOffset"
                type="range"
                aria-label="Top bound offset"
                :min="0"
                :max="Math.floor(imageHeight * 0.4)"
                class="slider-xs"
              >
            </div>
            <div class="bound-item">
              <span>B: {{ imageBounds.bottomOffset }}</span>
              <input
                v-model.number="imageBounds.bottomOffset"
                type="range"
                aria-label="Bottom bound offset"
                :min="0"
                :max="Math.floor(imageHeight * 0.4)"
                class="slider-xs"
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.threshold-tuning-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 15px;
}

.threshold-panel {
  background: #1a1a1a;
  border-radius: 8px;
  border: 2px solid #4fc3f7;
  max-width: 1600px;
  width: 100%;
  max-height: 95vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  border-bottom: 2px solid #4fc3f7;
  background: #252525;
}

.panel-header h2 {
  color: #4fc3f7;
  margin: 0;
  font-size: 18px;
}

.close-btn {
  background: transparent;
  border: none;
  color: #aaa;
  font-size: 24px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #333;
  color: #fff;
}

/* Compact Controls */
.control-section-compact {
  padding: 12px 15px;
  background: #2a2a2a;
  border-bottom: 1px solid #333;
}

.controls-row {
  display: flex;
  gap: 20px;
  align-items: start;
  margin-bottom: 10px;
}

.control-group-compact {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.flex-grow {
  flex: 1;
}

.label-compact {
  font-size: 10px;
  color: #bbb;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.hint-compact {
  font-size: 9px;
  color: #777;
}

/* Polynomial Order */
.order-buttons {
  display: flex;
  gap: 4px;
}

.order-btn {
  background: #444;
  color: #eee;
  border: 1px solid #666;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.15s;
  min-width: 30px;
}

.order-btn:hover {
  background: #555;
}

.order-btn.active {
  background: #4fc3f7;
  color: #000;
  border-color: #4fc3f7;
}

/* Sliders */
.slider-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.value-compact {
  color: #4fc3f7;
  font-weight: bold;
  font-size: 13px;
}

.slider-compact {
  width: 100%;
  height: 5px;
  border-radius: 2px;
  background: #444;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
}

.slider-compact::-webkit-slider-thumb {
  appearance: none;
  -webkit-appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #4fc3f7;
  cursor: pointer;
}

.slider-compact::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #4fc3f7;
  cursor: pointer;
  border: none;
}

.markers-compact {
  display: flex;
  justify-content: space-between;
  font-size: 9px;
  color: #666;
  margin-top: 2px;
}

/* Presets */
.preset-row {
  display: flex;
  gap: 4px;
}

.btn-xs {
  background: #444;
  color: #eee;
  border: 1px solid #666;
  padding: 4px 8px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 10px;
  transition: all 0.15s;
}

.btn-xs:hover {
  background: #555;
  border-color: #4fc3f7;
}

/* Bounds */
.bounds-compact {
  margin-top: 10px;
  padding: 10px;
  background: #333;
  border-radius: 6px;
}

.bounds-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.btn-reset {
  background: #444;
  color: #eee;
  border: 1px solid #666;
  padding: 3px 8px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 10px;
  transition: all 0.15s;
}

.btn-reset:hover {
  background: #555;
  border-color: #ffa726;
}

.bounds-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.bound-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 10px;
  color: #ddd;
}

.slider-xs {
  width: 100%;
  height: 3px;
  border-radius: 2px;
  background: #555;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
}

.slider-xs::-webkit-slider-thumb {
  appearance: none;
  -webkit-appearance: none;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ffa726;
  cursor: pointer;
}

.slider-xs::-moz-range-thumb {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ffa726;
  cursor: pointer;
  border: none;
}

/* Stats */
.stats-compact {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px;
  background: #333;
  border-radius: 4px;
  margin-top: 10px;
  font-size: 11px;
}

.stat-compact {
  color: #ddd;
}

.stat-compact strong {
  color: #66bb6a;
  margin-right: 4px;
}

/* Images */
.image-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  padding: 15px;
  background: #1a1a1a;
}

.image-panel {
  background: #2a2a2a;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #444;
}

.image-title-compact {
  color: #ffa726;
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 8px;
  text-align: center;
}

.range-hint {
  font-size: 10px;
  color: #66bb6a;
  font-weight: normal;
  margin-left: 6px;
}

.canvas-wrapper,
.canvas-wrapper-interactive {
  position: relative;
  width: 100%;
}

.canvas-wrapper-interactive {
  cursor: crosshair;
  user-select: none;
}

canvas {
  display: block;
  border: 2px solid #555;
  image-rendering: pixelated;
  width: 100%;
  height: auto;
}

.mask-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.boundary-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.boundary-line {
  stroke: #ffa500;
  stroke-width: 1;
  pointer-events: stroke;
  cursor: pointer;
  transition: all 0.2s;
}

.boundary-line:hover,
.boundary-active {
  stroke: #ffeb3b;
  stroke-width: 1;
}

.boundary-label {
  fill: #ffa500;
  font-size: 10px;
  font-weight: bold;
  pointer-events: none;
  text-shadow: 1px 1px 2px #000;
}

/* Legend */
.legend-compact {
  display: flex;
  gap: 12px;
  margin-top: 8px;
  padding: 6px;
  background: #333;
  border-radius: 4px;
  font-size: 10px;
  flex-wrap: wrap;
}

.legend-item-sm {
  display: flex;
  align-items: center;
  gap: 4px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #666;
  flex-shrink: 0;
}

.dot.green {
  background: rgba(0, 255, 0, 0.6);
}
.dot.red {
  background: rgba(255, 0, 0, 0.6);
}
.dot.orange {
  background: rgba(255, 165, 0, 0.6);
}

.gradient-bar {
  width: 50px;
  height: 12px;
  background: linear-gradient(to right, #00f, #fff, #f00);
  border: 1px solid #666;
  border-radius: 2px;
  margin-right: 6px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 15px;
  border-top: 1px solid #333;
  background: #252525;
}

.btn-secondary-sm,
.btn-primary-sm {
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-secondary-sm {
  background: #555;
  color: #eee;
}

.btn-secondary-sm:hover {
  background: #666;
}

.btn-primary-sm {
  background: #4fc3f7;
  color: #000;
}

.btn-primary-sm:hover {
  background: #81d4fa;
  transform: translateY(-1px);
}
</style>
